import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Encounter } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Paper, Stack, Title } from "@mantine/core";

import { fetchEncounterProceduresVersionChains } from "@/encounters/services/procedures-amendment";
import { fetchAmendmentHistory } from "@/encounters/services/questionnaire-response";
import { ProceduresAmendmentRow } from "@/encounters/types/procedures";
import { buildProceduresAmendmentRows } from "@/encounters/utils/procedures-amendment-diff";
import { resolveProceduresAmendmentWindow } from "@/encounters/utils/procedures-amendment-window";
import { DevelopmentFeatureFlags, useFeature } from "@/feature-flags";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import ProceduresAmendmentDiffCard from "./components/ProceduresAmendmentDiffCard";

type ProceduresAmendmentDiffProps = {
  encounter: Encounter;
  /** The amendment version the page is showing, from its own route. */
  versionId: string | undefined;
};

/**
 * What an amendment changed in the encounter's procedures tab.
 *
 * The tab writes Condition and Procedure resources directly, so this reads their version histories
 * rather than a QuestionnaireResponse's answers, and compares the versions on either side of the
 * amendment's window.
 *
 * Nothing renders when the amendment left the tab alone. That is deliberate rather than an empty
 * panel: the encounters that record procedures through the tab are the ones whose amendment pages
 * already carry a section per part of the encounter, and an amendment usually touches one of them.
 *
 * The gate lives here rather than at the five call sites. `EncounterTabs` puts the procedures tab
 * itself behind the same flag, and five wrappers would be five chances to leave one off.
 */
const ProceduresAmendmentDiff = ({ encounter, versionId }: ProceduresAmendmentDiffProps) => {
  const medplum = useMedplum();
  const { clientId } = useParams<{ clientId: string }>();
  const isProceduresTabEnabled = useFeature(DevelopmentFeatureFlags.PracticeBilling);

  const [rows, setRows] = useState<ProceduresAmendmentRow[]>([]);

  const encounterId = encounter.id;

  useEffect(() => {
    let cancelled = false;

    // Clear synchronously, before the fetch below settles, so a still-loading amendment never
    // shows the previous one's procedures, diagnoses, or notes in the meantime.
    setRows([]);

    const loadRows = async () => {
      if (!isProceduresTabEnabled || !clientId || !encounterId || !versionId) {
        return;
      }

      try {
        const amendmentHistory = await fetchAmendmentHistory(medplum, encounter);
        const amendmentWindow = resolveProceduresAmendmentWindow(amendmentHistory, versionId);

        // The version is not one of the encounter's amendments, so there is no window to read the
        // procedures at. The page's own header already says the version was not found.
        if (!amendmentWindow) {
          if (!cancelled) setRows([]);
          return;
        }

        const chains = await fetchEncounterProceduresVersionChains({
          medplum,
          clientId,
          encounterId,
        });

        // A newer effect run may have already landed its own rows for a different amendment or
        // encounter by the time this request resolves; applying this one now would show its
        // procedures under the wrong version.
        if (!cancelled) setRows(buildProceduresAmendmentRows({ window: amendmentWindow, chains }));
      } catch (error) {
        // Only the encounter's id reaches Sentry. What the encounter was diagnosed with is clinical;
        // a failure to display it is an operational problem. The section renders nothing rather than
        // breaking the rest of the amendment view.
        captureExceptionWithoutPhi(
          "Failed to read the encounter's procedures amendment diff",
          error,
          { encounterId },
        );
        if (!cancelled) setRows([]);
      }
    };

    void loadRows();

    return () => {
      cancelled = true;
    };
  }, [medplum, encounter, encounterId, clientId, versionId, isProceduresTabEnabled]);

  if (!rows.length) return null;

  return (
    <Paper p="md" withBorder data-testid="procedures-amendment-diff">
      <Stack mb="lg">
        <Title order={2}>Procedures</Title>
      </Stack>
      <Stack>
        {rows.map((row) => (
          <ProceduresAmendmentDiffCard row={row} key={row.procedureId} />
        ))}
      </Stack>
    </Paper>
  );
};

export default ProceduresAmendmentDiff;
